// data-fit LLC · DecisionFlow · EXECUTE Stage
// PayoutResultsViewer.jsx · DDL: R5 · API: v1.11 · Session 92
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: is_latest_final=true, generated fields read-only, numeric strings parsed
// PATCH-S92-07: OI comment added for USD currency hardcoding vs payout_currency_code per row
// M040 C2: hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
// S574 fix — OI-S573-PAYROLL-VIEW-FULL-NAME: router returns position_name (p.full_name aliased per
//   api_contract-vs-DDL drift note, payout_summaries_router.py line 49). Component was reading
//   row.full_name (undefined) → fell through to row.position_id (UUID). Fixed to row.position_name.
// S578 — search, sort, clickable rows + PayoutDetailPanel side panel.
//   Client-side search (position_name) + sort (all numeric/text columns).
//   Row click opens side panel fetching /api/v1/payout-summaries/{id} for full breakdown.
//   Panel pattern mirrors ManagerShell.jsx DetailSidePanel for consistency.

import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './executeFlowConstants'
import { X, ChevronUp, ChevronDown, ChevronsUpDown, Search } from 'lucide-react'

function getErrorMessage(payload) {
  const error = payload?.error
  if (!error) return 'Unexpected payout summary error.'
  if (error.code === 'AUTH_INSUFFICIENT_ROLE') return 'You do not have the comp_admin role required to view payouts.'
  if (error.code === 'FEATURE_UNAVAILABLE') return 'Payout results are unavailable for the current tenant tier.'
  return error.recovery_action || error.message || 'Unable to load payouts.'
}

function parseMoney(value) { const parsed = Number(value); return Number.isFinite(parsed) ? parsed : 0 }

function formatMoney(value, currencyCode) {
  // OI-EXEC-PAYOUT-CURRENCY: multi-currency Enterprise only; defaulting to USD for MVP
  const currency = currencyCode || 'USD'
  return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(parseMoney(value))
}

function formatPercent(value) { return `${(parseMoney(value) * 100).toFixed(2)}%` }

// ---------------------------------------------------------------------------
// SortIcon — visual indicator for current sort state on a column
// ---------------------------------------------------------------------------
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-1 text-[rgb(var(--color-text-muted))] opacity-40" />
  return sortDir === 'asc'
    ? <ChevronUp className="inline h-3 w-3 ml-1 text-[rgb(var(--color-primary))]" />
    : <ChevronDown className="inline h-3 w-3 ml-1 text-[rgb(var(--color-primary))]" />
}

// ---------------------------------------------------------------------------
// PayoutDetailPanel — slide-in side panel, mirrors ManagerShell DetailSidePanel
// Fetches /api/v1/payout-summaries/{payout_summary_id} on open.
// ---------------------------------------------------------------------------
function PayoutDetailPanel({ row, onClose }) {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const isOpen = !!row

  useEffect(() => {
    if (!row?.payout_summary_id) { setDetail(null); return }
    setLoading(true); setError(null); setDetail(null)
    fetch(`/api/v1/payout-summaries/${row.payout_summary_id}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)))
      .then(data => setDetail(data))
      .catch(() => setError('Unable to load payout detail.'))
      .finally(() => setLoading(false))
  }, [row?.payout_summary_id])

  const fmt = (v) => formatMoney(v, row?.payout_currency_code)
  const fmtPct = (v) => v != null ? `${(Number(v) * 100).toFixed(1)}%` : '—'

  // payout_as_pct_ote not in list response — compute from detail once loaded
  const payoutPctOte = useMemo(() => {
    if (detail?.total_payout && detail?.calculation_trace?.ote_midpoint) {
      const payout = Number(detail.total_payout)
      const ote    = Number(detail.calculation_trace.ote_midpoint)
      if (ote > 0) return `${((payout / ote) * 100).toFixed(2)}%`
    }
    // Fallback: use list row value if present (future-proof if router adds it)
    if (row?.payout_as_pct_ote != null) {
      return `${(Number(row.payout_as_pct_ote) * 100).toFixed(2)}%`
    }
    return loading ? '…' : '—'
  }, [detail, row, loading])

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <div className={`fixed top-0 right-0 z-40 h-full w-96 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Header */}
        <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
          <div className="min-w-0 pr-3">
            <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))] mb-0.5">Payout Detail</p>
            <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] truncate">
              {row?.position_name ?? '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))] transition"
            aria-label="Close panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
          )}
          {error && (
            <div className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">
              {error}
            </div>
          )}

          {/* Summary tiles from list row — available immediately, no fetch needed */}
          {row && (
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Total Payout', value: formatMoney(row.total_payout, row.payout_currency_code) },
                { label: 'Payout % OTE',  value: payoutPctOte },
                { label: 'ARR',           value: formatMoney(row.arr_payout, row.payout_currency_code) },
                { label: 'MBO',           value: formatMoney(row.mbo_payout, row.payout_currency_code) },
                { label: 'NPS',           value: formatMoney(row.nps_payout, row.payout_currency_code) },
                { label: 'Adoption',      value: formatMoney(row.adoption_payout, row.payout_currency_code) },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">{label}</p>
                  <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Component breakdown from detail fetch */}
          {detail?.components?.length > 0 && (
            <div>
              <p className="mb-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Component Breakdown</p>
              <div className="space-y-2">
                {detail.components.map((c, i) => (
                  <div key={c.payout_detail_id ?? i} className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{c.component_type}</span>
                      {/* resolved_payout is a numeric string — format with currency */}
                      <span className="text-tok-sm font-bold text-[rgb(var(--color-text))]">{formatMoney(c.resolved_payout, c.currency_code)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {c.component_type === 'ARR' && (<>
                        {c.quota == null ? (
                          <div className="col-span-3 text-[rgb(var(--color-text-muted))] italic">Rollup — attainment based on team credits</div>
                        ) : (<>
                          <div><span className="block">Quota</span>{c.quota}</div>
                          <div><span className="block">Credit</span>{c.credit ?? '—'}</div>
                          <div><span className="block">Attainment</span>{c.attainment_pct != null ? `${(Number(c.attainment_pct) * 100).toFixed(1)}%` : '—'}</div>
                        </>)}
                      </>)}
                      {c.component_type === 'MBO' && (<>
                        <div><span className="block">Code</span>{c.mbo_code ?? '—'}</div>
                        <div><span className="block">Scoring</span>{c.scoring_method ?? '—'}</div>
                        <div><span className="block">Achieved</span>{c.achieved_flag === true ? 'Yes' : c.achieved_flag === false ? 'No' : '—'}</div>
                      </>)}
                      {c.component_type !== 'ARR' && c.component_type !== 'MBO' && (
                        <div className="col-span-3 italic">No breakdown available</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Calculation trace */}
          {detail?.calculation_trace && (
            <div>
              <p className="mb-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">How It Was Calculated</p>
              <div className="space-y-1.5 text-tok-sm">
                {detail.calculation_trace.proof_steps?.map((step, i) => (
                  <div key={i} className={`flex justify-between py-1 ${step.is_result || step.is_total ? 'border-t border-[rgb(var(--color-border))] font-semibold' : ''}`}>
                    <span className="text-[rgb(var(--color-text-muted))]">{step.label}</span>
                    <span className={`${step.is_negative ? 'text-[rgb(var(--color-danger))]' : step.is_zero ? 'text-[rgb(var(--color-text-muted))]' : 'text-[rgb(var(--color-text))]'}`}>
                      {step.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const COLUMNS = [
  { key: 'position_name',          label: 'Position',        numeric: false },
  { key: 'arr_payout',             label: 'ARR',             numeric: true  },
  { key: 'mbo_payout',             label: 'MBO',             numeric: true  },
  { key: 'nps_payout',             label: 'NPS',             numeric: true  },
  { key: 'adoption_payout',        label: 'Adoption',        numeric: true  },
  { key: 'total_payout',           label: 'Total Payout',    numeric: true  },
  { key: 'payout_as_pct_ote',      label: 'Payout % OTE',   numeric: true  },
]

export default function PayoutResultsViewer({ fiscalPeriodId }) {
  const { token } = useAuth()

  const [rows, setRows]               = useState([])
  const [loading, setLoading]         = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [search, setSearch]           = useState('')
  const [sortCol, setSortCol]         = useState('position_name')
  const [sortDir, setSortDir]         = useState('asc')
  const [selectedRow, setSelectedRow] = useState(null)
  // S616 — acknowledgment status map: position_id → bool
  const [ackMap, setAckMap]           = useState({})

  const periodId = useMemo(() => {
    const parsed = Number(fiscalPeriodId)
    return Number.isInteger(parsed) ? parsed : null
  }, [fiscalPeriodId])

  const loadPayouts = async () => {
    if (!periodId) { setErrorMessage('A valid integer fiscal_period_id is required.'); return }
    setLoading(true); setErrorMessage('')
    try {
      const response = await fetch(
        `/api/v1/payout-summaries?fiscal_period_id=${encodeURIComponent(periodId)}&is_latest_final=true&limit=500`,
        { credentials: 'include' }
      )
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(getErrorMessage(payload))
      setRows(Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [])
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load payout summaries.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPayouts()
    // S616 — fetch acknowledgment status for all positions in this period
    if (periodId) {
      fetch(
        `/api/v1/plan-acknowledgments?fiscal_period_id=${encodeURIComponent(periodId)}&limit=500`,
        { credentials: 'include' }
      )
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data?.data) return
          const map = {}
          for (const r of data.data) { map[r.position_id] = r.acknowledged }
          setAckMap(map)
        })
        .catch(() => {}) // non-fatal — dot simply won't show on fetch failure
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId])

  const totals = useMemo(() => rows.reduce((acc, row) => {
    acc.arr      += parseMoney(row.arr_payout)
    acc.mbo      += parseMoney(row.mbo_payout)
    acc.nps      += parseMoney(row.nps_payout)
    acc.adoption += parseMoney(row.adoption_payout)
    acc.total    += parseMoney(row.total_payout)
    return acc
  }, { arr: 0, mbo: 0, nps: 0, adoption: 0, total: 0 }), [rows])

  // Client-side search + sort
  const displayRows = useMemo(() => {
    let filtered = rows
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      filtered = rows.filter(r =>
        (r.position_name ?? r.position_id ?? '').toLowerCase().includes(q)
      )
    }
    return [...filtered].sort((a, b) => {
      const col = COLUMNS.find(c => c.key === sortCol)
      const av = col?.numeric ? parseMoney(a[sortCol]) : (a[sortCol] ?? '').toLowerCase()
      const bv = col?.numeric ? parseMoney(b[sortCol]) : (b[sortCol] ?? '').toLowerCase()
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [rows, search, sortCol, sortDir])

  const handleSort = useCallback((key) => {
    setSortCol(prev => {
      if (prev === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return key }
      setSortDir('asc'); return key
    })
  }, [])

  const goNext = () => {
    window.dispatchEvent(new CustomEvent('decisionflow:navigate', { detail: { screen: SCREEN_ORDER[4] } }))
  }

  return (
    <section className="space-y-6">
      <header>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">EXEC-04</p>
        <h2 className="text-tok-2xl font-bold text-[rgb(var(--color-text))]">Payout Results Viewer</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">Displays authoritative payout-of-record rows using is_latest_final=true. Click a row for full detail.</p>
      </header>

      {/* Toolbar — refresh + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={loadPayouts}
          disabled={loading}
          className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh payouts'}
        </button>
        {rows.length > 0 && (
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input
              type="text"
              placeholder="Search position…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-3 py-2 rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] w-52"
            />
          </div>
        )}
        {search && (
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {displayRows.length} of {rows.length} positions
          </p>
        )}
      </div>

      {errorMessage && (
        <div className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">
          {errorMessage}
        </div>
      )}
      {loading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading payout summaries…</p>}

      {!loading && !errorMessage && rows.length === 0 && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No latest final payout summaries were returned.</p>
      )}

      {rows.length > 0 && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[
              { label: 'ARR',                  value: totals.arr      },
              { label: 'MBO',                  value: totals.mbo      },
              { label: 'NPS',                  value: totals.nps      },
              { label: 'Adoption',             value: totals.adoption },
              { label: 'Total generated payout', value: totals.total  },
            ].map(({ label, value }) => (
              <div key={label} className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-4">
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-1">{label}</p>
                <p className="text-tok-lg font-semibold text-[rgb(var(--color-text))]">{formatMoney(value)}</p>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-22rem)] bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg">
            <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
              <thead className="bg-[rgb(var(--color-surface-muted))] sticky top-0 z-10">
                <tr>
                  {COLUMNS.map(({ key, label }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider cursor-pointer select-none hover:text-[rgb(var(--color-text))] transition-colors"
                    >
                      {label}
                      <SortIcon col={key} sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-4 py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))] italic">
                      No positions match "{search}"
                    </td>
                  </tr>
                ) : (
                  displayRows.map((row) => (
                    <tr
                      key={row.payout_summary_id || row.position_id}
                      onClick={() => setSelectedRow(row)}
                      className={`cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${selectedRow?.payout_summary_id === row.payout_summary_id ? 'bg-[rgb(var(--color-primary))]/8' : ''}`}
                    >
                      <td className="px-4 py-2 text-[rgb(var(--color-text))] font-medium text-tok-sm">
                        <span className="flex items-center gap-2">
                          {ackMap[row.position_id] === false && (
                            <span
                              className="h-3 w-3 shrink-0 rounded-full bg-[rgb(var(--color-warning))]"
                              title="Plan not yet acknowledged"
                            />
                          )}
                          {row.position_name ?? row.employee_id ?? row.position_id ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">{formatMoney(row.arr_payout,      row.payout_currency_code)}</td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">{formatMoney(row.mbo_payout,      row.payout_currency_code)}</td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">{formatMoney(row.nps_payout,      row.payout_currency_code)}</td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">{formatMoney(row.adoption_payout, row.payout_currency_code)}</td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">
                        <span title="GENERATED COLUMN — read-only">{formatMoney(row.total_payout, row.payout_currency_code)}</span>
                      </td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">
                        <span title="GENERATED COLUMN — read-only">
                          {row.ote_reporting && Number(row.ote_reporting) > 0
                            ? `${((Number(row.total_payout) / Number(row.ote_reporting)) * 100).toFixed(2)}%`
                            : '—'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* OI-EXEC-PAYOUT-DETAILS-EXISTENCE: detail fetch uses payout_summary_id confirmed in list response */}

          {/* Acknowledgment legend — shown when any unacknowledged reps exist */}
          {Object.values(ackMap).some(v => v === false) && (
            <p className="mt-2 flex items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
              <span className="h-3 w-3 shrink-0 rounded-full bg-[rgb(var(--color-warning))]" />
              Reps marked with this indicator have not yet acknowledged their compensation plan — payout figures are hidden from them until they do.
            </p>
          )}
        </>
      )}

      <footer className="pt-4 border-t border-[rgb(var(--color-border))]">
        <button
          type="button"
          onClick={goNext}
          disabled={rows.length === 0}
          className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
        >
          Continue to statement batches
        </button>
      </footer>

      {/* Side panel — outside section flow, fixed position */}
      <PayoutDetailPanel
        row={selectedRow}
        onClose={() => setSelectedRow(null)}
      />
    </section>
  )
}
